/**
 * Pure clustering for Component Matching Import report.
 * Groups rows by issue type (Work Patterns / Pay Components pattern).
 */
import type {
  MatchingImportError,
  MatchingImportWarning,
  MatchingRow,
} from "../types/matching";

export type MatchingReportTab = "all" | "errors" | "warnings" | "edited" | "passed";

export type MatchingRowStatus =
  | "ready"
  | "passed"
  | "imported"
  | "error"
  | "warning"
  | "edited"
  | "not_evaluated";

export type MatchingClusterId =
  | "edited"
  | "duplicate"
  | "employeeNotFound"
  | "unknownComponent"
  | "ambiguousComponent"
  | "unsupportedManualPay"
  | "payRunMismatch"
  | "payRunNotWritable"
  | "missingRequired"
  | "catchAll"
  | "warning"
  | "passed";

export type MatchingClusterKind = "error" | "warning" | "edited" | "passed";

export interface MatchingClusterConfig {
  id: MatchingClusterId;
  kind: MatchingClusterKind;
  title: string;
  hint: string;
}

const ERROR_CLUSTER_ORDER: MatchingClusterId[] = [
  "duplicate",
  "employeeNotFound",
  "unknownComponent",
  "ambiguousComponent",
  "unsupportedManualPay",
  "payRunMismatch",
  "payRunNotWritable",
  "missingRequired",
  "catchAll",
];

/** Display order: edited first (needs re-validate), then errors, then warnings, then passed. */
const CLUSTER_DISPLAY_ORDER: MatchingClusterId[] = [
  "edited",
  ...ERROR_CLUSTER_ORDER,
  "warning",
  "passed",
];

export const MATCHING_CLUSTER_CONFIGS: Record<
  MatchingClusterId,
  MatchingClusterConfig
> = {
  edited: {
    id: "edited",
    kind: "edited",
    title: "Edited since validation",
    hint: "These rows changed after the last validation. Re-validate to refresh their checks.",
  },
  duplicate: {
    id: "duplicate",
    kind: "error",
    title: "Duplicate employee + component + dates",
    hint: "Keep one row per employee/component/date span in this file, then re-validate.",
  },
  employeeNotFound: {
    id: "employeeNotFound",
    kind: "error",
    title: "Employee not found",
    hint: "Check the Employee Code exists on the bound pay run.",
  },
  unknownComponent: {
    id: "unknownComponent",
    kind: "error",
    title: "Unknown component name",
    hint: "Use a component name from the company catalog, then re-validate.",
  },
  ambiguousComponent: {
    id: "ambiguousComponent",
    kind: "error",
    title: "Could not identify target component",
    hint: "Supply StartDate and FinishDate to pick one assignment span, or fix duplicates on Datapay desktop.",
  },
  unsupportedManualPay: {
    id: "unsupportedManualPay",
    kind: "error",
    title: "Manual pay not supported",
    hint: "This component cannot take a manual pay override. Remove the row or pick another component.",
  },
  payRunMismatch: {
    id: "payRunMismatch",
    kind: "error",
    title: "Pay run mismatch",
    hint: "Row PayRunId must match the bound pay run for this import.",
  },
  payRunNotWritable: {
    id: "payRunNotWritable",
    kind: "error",
    title: "Pay run not writable",
    hint: "Use a Pending or In Progress pay run, then re-validate.",
  },
  missingRequired: {
    id: "missingRequired",
    kind: "error",
    title: "Missing required value",
    hint: "Fill the required field in the file, then upload again.",
  },
  catchAll: {
    id: "catchAll",
    kind: "error",
    title: "Validation error",
    hint: "Fix the highlighted row, then re-validate.",
  },
  warning: {
    id: "warning",
    kind: "warning",
    title: "Warnings",
    hint: "Review these rows. Dismiss a warning if you still want to import.",
  },
  passed: {
    id: "passed",
    kind: "passed",
    title: "Passed",
    hint: "",
  },
};

export interface ClusteredMatchingRow {
  index: number;
  row: MatchingRow;
  status: MatchingRowStatus;
  errors: string[];
  warnings: string[];
  clusterId: MatchingClusterId;
}

export interface MatchingClusterGroup {
  clusterId: MatchingClusterId;
  config: MatchingClusterConfig;
  rows: ClusteredMatchingRow[];
}

export interface MatchingTabCounts {
  all: number;
  errors: number;
  warnings: number;
  edited: number;
  passed: number;
}

export function classifyMatchingErrorCluster(
  message: string
): MatchingClusterId {
  const lower = message.toLowerCase();
  // Ambiguous before file-duplicate: API R-APP-3c copy mentions
  // "fix duplicate assignments on Datapay desktop", which must not land in
  // the in-file duplicate cluster.
  if (
    /could not accurately identify|more than one pay-packet line|ambiguous/.test(
      lower
    )
  ) {
    return "ambiguousComponent";
  }
  if (
    /duplicate row in this file|same employee, component, and assignment dates|keep one row per (line identity|employee)/.test(
      lower
    )
  ) {
    return "duplicate";
  }
  if (/employee .*not found|was not found in the bound pay run/.test(lower)) {
    return "employeeNotFound";
  }
  if (
    /component name could not be resolved|unknown component/.test(lower)
  ) {
    return "unknownComponent";
  }
  if (/does not support the manual pay|manual pay override/.test(lower)) {
    return "unsupportedManualPay";
  }
  if (/matching is blocked while this pay run is/.test(lower)) {
    return "payRunNotWritable";
  }
  if (/payrunid|pay run/.test(lower) && /match|mismatch|bound/.test(lower)) {
    return "payRunMismatch";
  }
  if (
    /required|missing|must (be|have)|blank|empty|is required|nothing retained/.test(
      lower
    )
  ) {
    return "missingRequired";
  }
  return "catchAll";
}

export function classifyPrimaryMatchingCluster(
  messages: readonly string[]
): MatchingClusterId {
  if (messages.length === 0) return "passed";
  const ids = messages.map(classifyMatchingErrorCluster);
  for (const id of ERROR_CLUSTER_ORDER) {
    if (ids.includes(id)) return id;
  }
  return "catchAll";
}

/**
 * Clusters rows by issue type. `editedIndices` are 0-based row indexes
 * changed since the last validation (via the row-edit modal) — mirrors Pay
 * Component Import's `editedRowIndexes`: while a row is flagged edited, it
 * is shown in its own "Edited since validation" group/tab rather than its
 * (now-stale) error or passed classification, since the stale checks can no
 * longer be trusted. Once re-validation succeeds, the caller clears
 * `editedIndices` and the row falls back to a fresh error/warning/passed
 * classification from the new result.
 */
export function clusterMatchingRows(
  rows: MatchingRow[],
  errors: MatchingImportError[],
  warnings: MatchingImportWarning[],
  phase: "idle" | "validated" | "committed",
  validationStale: boolean,
  editedIndices?: ReadonlySet<number> | readonly number[]
): ClusteredMatchingRow[] {
  const edited =
    editedIndices instanceof Set
      ? editedIndices
      : new Set(editedIndices ?? []);

  const errorsByIndex = new Map<number, string[]>();
  for (const err of errors) {
    if (err.index === null) continue;
    const list = errorsByIndex.get(err.index) ?? [];
    list.push(err.error);
    errorsByIndex.set(err.index, list);
  }
  const warningsByIndex = new Map<number, string[]>();
  for (const w of warnings) {
    for (const idx of w.indices) {
      const list = warningsByIndex.get(idx) ?? [];
      list.push(w.warning);
      warningsByIndex.set(idx, list);
    }
  }

  return rows.map((row, index) => {
    const rowErrors = errorsByIndex.get(index) ?? [];
    const rowWarnings = warningsByIndex.get(index) ?? [];

    if (edited.has(index)) {
      return {
        index,
        row,
        status: "edited",
        errors: rowErrors,
        warnings: rowWarnings,
        clusterId: "edited",
      };
    }

    let status: MatchingRowStatus = "not_evaluated";
    let clusterId: MatchingClusterId = "passed";

    if (rowErrors.length > 0) {
      status = "error";
      clusterId = classifyPrimaryMatchingCluster(rowErrors);
    } else if (rowWarnings.length > 0) {
      // Keep Warning status/cluster even after commit or while stale — otherwise
      // the Checks badge paints yellow (warnings present) but labels
      // "Imported"/"Ready", which is inconsistent with the report tabs.
      status = "warning";
      clusterId = "warning";
    } else if (phase === "committed") {
      status = "imported";
      clusterId = "passed";
    } else if (phase === "validated" && !validationStale) {
      status = "passed";
      clusterId = "passed";
    } else if (validationStale || phase === "idle") {
      status = "ready";
      clusterId = "passed";
    }

    return {
      index,
      row,
      status,
      errors: rowErrors,
      warnings: rowWarnings,
      clusterId,
    };
  });
}

export function groupMatchingRowsByClusters(
  clustered: readonly ClusteredMatchingRow[]
): MatchingClusterGroup[] {
  const map = new Map<MatchingClusterId, ClusteredMatchingRow[]>();
  for (const item of clustered) {
    // Error rows can carry multiple findings — fan out into every matching
    // error cluster so each issue type is visible (e.g. duplicate + missing).
    // Edited / warning / passed keep a single primary clusterId.
    const membershipIds: MatchingClusterId[] =
      item.status === "error" && item.errors.length > 0
        ? [...new Set(item.errors.map(classifyMatchingErrorCluster))]
        : [item.clusterId];

    for (const id of membershipIds) {
      const list = map.get(id) ?? [];
      list.push(item);
      map.set(id, list);
    }
  }

  const groups: MatchingClusterGroup[] = [];
  const hasBlocking =
    ERROR_CLUSTER_ORDER.some((id) => (map.get(id)?.length ?? 0) > 0) ||
    (map.get("edited")?.length ?? 0) > 0;

  for (const id of CLUSTER_DISPLAY_ORDER) {
    const clusterRows = map.get(id);
    if (!clusterRows?.length) continue;

    if (id === "passed") {
      // All rows in this cluster share one status per clusterMatchingRows'
      // phase-driven assignment, except errors (which live in other
      // clusters) — so the first row's status is representative.
      const isImported = clusterRows[0]?.status === "imported";
      const title = isImported
        ? `${clusterRows.length} imported`
        : hasBlocking
          ? `${clusterRows.length} ready to import`
          : `${clusterRows.length} passed`;
      groups.push({
        clusterId: id,
        config: { ...MATCHING_CLUSTER_CONFIGS.passed, title },
        rows: clusterRows,
      });
      continue;
    }

    if (id === "warning") {
      groups.push({
        clusterId: id,
        config: {
          ...MATCHING_CLUSTER_CONFIGS.warning,
          title:
            clusterRows.length === 1
              ? "1 warning"
              : `${clusterRows.length} warnings`,
        },
        rows: clusterRows,
      });
      continue;
    }

    if (id === "edited") {
      groups.push({
        clusterId: id,
        config: {
          ...MATCHING_CLUSTER_CONFIGS.edited,
          title:
            clusterRows.length === 1
              ? "1 row edited since validation"
              : `${clusterRows.length} rows edited since validation`,
        },
        rows: clusterRows,
      });
      continue;
    }

    groups.push({
      clusterId: id,
      config: MATCHING_CLUSTER_CONFIGS[id],
      rows: clusterRows,
    });
  }
  return groups;
}

export function countMatchingTabs(
  clustered: readonly ClusteredMatchingRow[]
): MatchingTabCounts {
  return {
    all: clustered.length,
    errors: clustered.filter((c) => c.status === "error").length,
    // Count warning-status rows only — error rows may also carry warnings but
    // belong on the Errors tab (and fan out into error clusters).
    warnings: clustered.filter((c) => c.status === "warning").length,
    edited: clustered.filter((c) => c.status === "edited").length,
    passed: clustered.filter(
      (c) =>
        c.status === "passed" ||
        c.status === "imported" ||
        c.status === "ready"
    ).length,
  };
}

export function filterByEmployeeCode(
  clustered: ClusteredMatchingRow[],
  query: string
): ClusteredMatchingRow[] {
  const q = query.trim().toLowerCase();
  if (!q) return clustered;
  return clustered.filter((c) =>
    c.row.employeeCode.toLowerCase().includes(q)
  );
}

export function filterByTab(
  clustered: ClusteredMatchingRow[],
  tab: MatchingReportTab
): ClusteredMatchingRow[] {
  switch (tab) {
    case "errors":
      return clustered.filter((c) => c.status === "error");
    case "warnings":
      return clustered.filter((c) => c.status === "warning");
    case "edited":
      return clustered.filter((c) => c.status === "edited");
    case "passed":
      return clustered.filter(
        (c) =>
          c.status === "passed" ||
          c.status === "imported" ||
          c.status === "ready"
      );
    default:
      return clustered;
  }
}

export function getVisibleMatchingGroups(
  tab: MatchingReportTab,
  groups: readonly MatchingClusterGroup[]
): MatchingClusterGroup[] {
  if (tab === "all") return [...groups];
  if (tab === "errors") {
    return groups.filter((g) => g.config.kind === "error");
  }
  if (tab === "warnings") {
    return groups.filter((g) => g.config.kind === "warning");
  }
  if (tab === "edited") {
    return groups.filter((g) => g.config.kind === "edited");
  }
  return groups.filter((g) => g.config.kind === "passed");
}

export function firstMatchingClusterIdForTab(
  tab: MatchingReportTab,
  groups: readonly MatchingClusterGroup[]
): MatchingClusterId | null {
  const visible = getVisibleMatchingGroups(tab, groups);
  return visible[0]?.clusterId ?? null;
}

/**
 * Search across row fields plus pay-run catalogue meta (id, pay group,
 * period end as shown in the Option B section header).
 */
export function filterMatchingGroupsBySearch(
  groups: readonly MatchingClusterGroup[],
  query: string,
  resolvePayRunMeta?: (payRunId: string) => {
    payGroupName?: string;
    periodEnd?: string;
  } | null
): MatchingClusterGroup[] {
  const q = query.trim().toLowerCase();
  if (!q) return [...groups];
  return groups
    .map((group) => {
      const rows = group.rows.filter((item) => {
        const row = item.row;
        const meta = resolvePayRunMeta?.(row.payRunId) ?? null;
        const periodEndIso = meta?.periodEnd ?? "";
        const periodEndUnpadded = periodEndIso
          ? formatMatchingSearchPeriodEnd(periodEndIso)
          : "";
        const periodEndPadded = periodEndIso
          ? formatMatchingSearchPeriodEndPadded(periodEndIso)
          : "";
        const haystack = [
          String(row.fileRow),
          row.payRunId,
          row.matchAction,
          row.rawMatchAction,
          row.employeeCode,
          row.componentCode ?? "",
          row.componentName,
          row.startDate ?? "",
          row.finishDate ?? "",
          row.targetAmount != null ? String(row.targetAmount) : "",
          row.rawAmount,
          meta?.payGroupName ?? "",
          periodEndIso,
          periodEndUnpadded,
          periodEndPadded,
          ...item.errors,
          ...item.warnings,
        ]
          .join(" ")
          .toLowerCase();
        return haystack.includes(q);
      });
      if (rows.length === 0) return null;
      return {
        ...group,
        rows,
        config: {
          ...group.config,
          title: retitleMatchingCluster(group.clusterId, group.config, rows),
        },
      };
    })
    .filter((group): group is MatchingClusterGroup => group !== null);
}

function retitleMatchingCluster(
  clusterId: MatchingClusterId,
  config: MatchingClusterConfig,
  rows: readonly ClusteredMatchingRow[]
): string {
  if (clusterId === "warning") {
    return rows.length === 1 ? "1 warning" : `${rows.length} warnings`;
  }
  if (clusterId === "edited") {
    return rows.length === 1
      ? "1 row edited since validation"
      : `${rows.length} rows edited since validation`;
  }
  if (clusterId === "passed") {
    const isImported = rows[0]?.status === "imported";
    return isImported
      ? `${rows.length} imported`
      : `${rows.length} ready to import`;
  }
  return config.title;
}

/** Match the report's unpadded `d/M/yyyy` period-end display for search. */
export function formatMatchingSearchPeriodEnd(isoDate: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!m) return isoDate;
  return `${Number(m[3])}/${Number(m[2])}/${m[1]}`;
}

/** Zero-padded `dd/MM/yyyy` variant so searches like `06/10/2026` still hit. */
export function formatMatchingSearchPeriodEndPadded(isoDate: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!m) return isoDate;
  return `${m[3]}/${m[2]}/${m[1]}`;
}

export const filterMatchingGroupsByEmployeeCode = filterMatchingGroupsBySearch;

/**
 * Add a 0-based row index to an edited-indices set, mirroring Pay Component
 * Import's `addEditedRowIndex`. Returns a new array (not a Set) so callers
 * can store it in simple component state and compare by value in tests.
 */
export function addEditedMatchingIndex(
  current: ReadonlySet<number> | readonly number[],
  index: number
): Set<number> {
  const next = new Set(current);
  next.add(index);
  return next;
}

/** Add multiple 0-based row indexes in one state update (bulk MatchAction). */
export function addEditedMatchingIndices(
  current: ReadonlySet<number> | readonly number[],
  indices: ReadonlySet<number> | readonly number[]
): Set<number> {
  const next = new Set(current);
  for (const index of indices) {
    next.add(index);
  }
  return next;
}

/**
 * Pre-commit summary banner copy — mirrors Work Patterns'
 * `formatBlockedImportBanner`, adapted to Matching's "row(s)" vocabulary.
 * `importableCount` is the number of rows without a blocking error (i.e.
 * eligible for the partial-import commit), which may still include warned
 * or edited-but-unvalidated rows.
 */
export function formatMatchingBlockedBanner(
  errorCount: number,
  totalCount: number,
  importableCount: number
): string {
  const totalLabel = totalCount === 1 ? "row" : "rows";
  const errorPhrase = errorCount === 1 ? "has an error" : "have errors";
  const main = `${errorCount} of ${totalCount} ${totalLabel} ${errorPhrase}.`;

  if (importableCount === 0) {
    return `${main} Rows with errors can't be imported — fix the file and upload again.`;
  }

  const errorLabel = errorCount === 1 ? "row" : "rows";
  const importableLabel = importableCount === 1 ? "row" : "rows";
  return `${main} ${importableCount} ${importableLabel} can still be imported; ${errorCount} ${errorLabel} with errors will be skipped.`;
}
